import { useQuery } from "@tanstack/react-query";
import { publicApi } from "@/lib/api/public-client";
import { searchApi } from "@/lib/api/search-client";
import { pascalToCamelCaseObject, snakeToCamelCaseObject } from "@/lib/utils";
import type { Category, CategoryDetailsResponse, CategoryProductsParams, CategoryResponse, Manufacturer } from "@/lib/types/catalog";
import { publicEndpoints } from "@/lib/endpoints/public";
import { defaultQueryOptions } from "@/lib/react-query-config";
import type { Picture, ProductOverview } from "@/lib/types/product";
import type { SearchFacet } from "@/lib/types/search";

const getCategories = async (loadImages?: boolean) => {
    const endpoint = publicEndpoints.catalog.getCategories(loadImages);
    const response = await publicApi.get<unknown>(endpoint);
    return snakeToCamelCaseObject<Category[]>(response.data);
}

const getCategory = async (categoryId: number, params?: Partial<CategoryProductsParams>) => {
    const endpoint = publicEndpoints.catalog.getCategory(categoryId);
    const payload: CategoryProductsParams = { ...(params ?? {}) } as CategoryProductsParams;
    const response = await publicApi.post<CategoryDetailsResponse>(endpoint, payload);
    const data = response.data;
    if (data?.category_model_dto?.sub_categories?.length) {
        data.category_model_dto.sub_categories = snakeToCamelCaseObject<Category[]>(
            data.category_model_dto.sub_categories as unknown as Record<string, unknown>[]
        );
    }
    if (data?.category_model_dto?.picture_model) {
        data.category_model_dto.picture_model = snakeToCamelCaseObject<Picture>(
            data.category_model_dto.picture_model as unknown as Record<string, unknown>
        );
    }
    return data;
}

const getProductsByCategoryId = async (categoryId: number, params?: Partial<CategoryProductsParams>) => {
    const endpoint = publicEndpoints.catalog.getProducts(categoryId);
    const payload: CategoryProductsParams = { ...(params ?? {}) } as CategoryProductsParams;
    const response = await publicApi.post<CategoryResponse>(endpoint, payload);
    return response.data;
}

const getSubCategories = async (categoryId: number) => {
    const endpoint = publicEndpoints.catalog.getSubCategories(categoryId);
    const response = await publicApi.get<unknown>(endpoint);
    return snakeToCamelCaseObject<Category[]>(response.data);
}

const getProductsByCategory = async (categoryIds: number[] | number, params?: Partial<CategoryProductsParams>): Promise<ProductSearchResponse> => {
    const categoryIdArray = Array.isArray(categoryIds) ? categoryIds : [categoryIds];
    // Filter out 0 values and only use valid category IDs
    const validCategoryIds = categoryIdArray.filter(id => id > 0);
    
    // Parse price range from "min-max" string format to separate priceMin and priceMax
    let priceMin: number | undefined;
    let priceMax: number | undefined;
    if (params?.price) {
        const [min, max] = params.price.split('-').map(Number);
        priceMin = !isNaN(min) ? min : undefined;
        priceMax = !isNaN(max) ? max : undefined;
    }
    
    const endpoint = publicEndpoints.products.searchWithFacets({
        categoryIds: validCategoryIds.length > 0 ? validCategoryIds : undefined,
        pageIndex: (params?.pagenumber ?? 1) - 1, // Convert from 1-based (UI) to 0-based (API)
        pageSize: params?.pagesize ?? 12,
        orderBy: params?.orderby,
        manufacturerIds: params?.ms,
        priceMin,
        priceMax,
        productTagIds: params?.productTagIds,
    });
    
    const response = await searchApi.get<unknown>(endpoint);
    // Convert PascalCase API response to camelCase
    return pascalToCamelCaseObject<ProductSearchResponse>(response.data);
}

// Export the response type for use in components
export interface ProductSearchResponse {
    totalCount: number;
    pageIndex: number;
    pageSize: number;
    totalPages: number;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
    items: ProductOverview[];
    categoryFacets?: SearchFacet[];
    manufacturerFacets?: SearchFacet[];
    productTagFacets?: SearchFacet[];
    priceRangeFacet?: {
        minPrice: number;
        maxPrice: number;
        buckets: Array<{ from: number; to: number; count: number; label: string }>;
    };
}

export const useCategories = (options?: { loadImages?: boolean }) => {
    return useQuery({
        queryKey: ['categories', options?.loadImages ?? false],
        queryFn: () => getCategories(options?.loadImages),
    });
}

export const useCategory = (
    categoryId: number, 
    options?: { enabled?: boolean }
) => {
    return useQuery({
        queryKey: ['category', categoryId],
        queryFn: () => getCategory(categoryId),
        enabled: options?.enabled ?? true,
    });
};

export const useSubCategories = (
    categoryId: number,
    options?: { enabled?: boolean }
) => {
    return useQuery({
        queryKey: ['catalog', 'sub-categories', categoryId],
        queryFn: () => getSubCategories(categoryId),
        enabled: options?.enabled ?? true,
    });
}

export const useProductsByCategoryId = (
    categoryId: number,
    options?: { enabled?: boolean; params?: Partial<CategoryProductsParams> }
) => {
    return useQuery({
        queryKey: ['catalog', 'products', categoryId, options?.params ?? {}],
        queryFn: () => getProductsByCategoryId(categoryId, options?.params),
        enabled: options?.enabled ?? true,
        staleTime: defaultQueryOptions.staleTime, 
    });
}

export const useCustomProductsByCategoryId = (
  categoryIds: number[] | number,
  options?: { enabled?: boolean; params?: Partial<CategoryProductsParams> }
  
) => {
  return useQuery({
    queryKey: ['catalog', 'products', Array.isArray(categoryIds) ? categoryIds.join('-') : categoryIds, options?.params ?? {}],
      queryFn: () => getProductsByCategory(categoryIds, options?.params),
    enabled: options?.enabled ?? true,
    staleTime: defaultQueryOptions.staleTime,
  });
};


export const useManufacturers = () => {
  return useQuery<Manufacturer[]>({
    queryKey: ["manufacturers"],
    queryFn: async () => {
      const response = await publicApi.get(
        publicEndpoints.catalog.getManufacturer()
      );
      return response.data as Manufacturer[];
    },
  });
};
